import { useEffect, useMemo, useState } from "react";
import type { FormField } from "../../components/AdminDataModal";
import type { Column } from "../../components/DataTable";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";
import DataTable from "../../components/DataTable";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";

export default function SMTPSettings() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const fetchSMTPs = async () => {
    try {
      const res = await fetch(`${BASE}/admin/email-settings/smtp`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = Array.isArray(result.data)
        ? result.data
        : result.data
          ? [result.data]
          : [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch SMTP Settings Error:", error);
    }
  };

  useEffect(() => {
    fetchSMTPs();
  }, []);

  const handleSubmit = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE}/admin/email-settings/smtp`
          : `${BASE}/admin/email-settings/smtp/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          host: formData.host,
          port: formData.port,
          username: formData.username,
          password: formData.password,
          from_email: formData.from_email,
          encryption: formData.encryption || "tls",
          status: formData.status || "active",
        }),
      });

      const result = await res.json();

      if (result.success) {
        fetchSMTPs();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "SMTP account added successfully."
              : "SMTP account updated successfully.",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Failed to save SMTP account.",
        });
      }
    } catch (error) {
      console.error("Submit SMTP Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message: "Failed to save SMTP account.",
      });
    }
  };

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete SMTP Account",
      message: "Are you sure you want to delete this SMTP account?",
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE}/admin/email-settings/smtp/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            fetchSMTPs();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "SMTP account deleted successfully.",
            });
          }
        } catch (error) {
          console.error("Delete SMTP Error:", error);
        }
      },
    });
  };

  const smtpFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },
      { key: "host", label: "SMTP Host", colSpan: 2 },
      { key: "port", label: "SMTP Port", colSpan: 1 },
      {
        key: "encryption",
        label: "Encryption",
        type: "select",
        options: [
          { label: "TLS", value: "tls" },
          { label: "SSL", value: "ssl" },
        ],
        colSpan: 1,
      },
      { key: "username", label: "Username", colSpan: 2 },
      {
        key: "password",
        label: "Password",
        type: "password",
        colSpan: 2,
      },
      { key: "from_email", label: "From Email", colSpan: 2 },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    { key: "host", label: "Host" },
    { key: "port", label: "Port" },
    { key: "username", label: "Username" },
    { key: "from_email", label: "From Email" },
    {
      key: "encryption",
      label: "Encryption",
      render: (row: any) => String(row.encryption || "").toUpperCase(),
    },
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row: any) =>
        row.created_at ? formatDateToDMY(row.created_at) : "-",
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="SMTP Settings"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setModalOpen(true);
          setActiveRow({
            host: "",
            port: "",
            username: "",
            password: "",
            from_email: "",
            encryption: "tls",
            status: "active",
          });
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="SMTP Account"
        fields={smtpFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
